'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, ChevronDown, ChevronUp, ArrowLeft, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { IconArrowHeadDown, IconArrowRight, IconSearch, IconX } from "@/templates/Heritage/utils/icons";

import LocationDetail from './LocationDetail';
import { sceneServices } from '@/templates/Heritage/utils/services/scene';
import { useLanguage } from '@/contexts/I18nProvider';
import { useIdPath } from '@/contexts/IdPathProvider';
import minimapServices from '@/templates/Heritage/utils/services/minimap';
import {
    resolveMarkerPresentation,
    NumberBadgeView,
    ResolvedMarkerPresentation,
    findCategoryChain,
    isSceneMatching,
    isLocationMatchingScene,
} from '@/utils/minimapMarkerDisplay';

// Define structures for the nested data
export interface LocationItem {
    id: string;
    title: string;
    description?: string;
    image?: string;
    address?: string;
    // Add other fields as needed for navigation
    [key: string]: any;
}

export interface LocationCategory {
    id: string;
    title: string;
    items: LocationItem[];
    children: LocationCategory[];
}

interface ListLocationProps {
    minimapId?: number;
    mapMarkerDisplay?: unknown;
    mapLocations?: any[];
    currentScene?: string;
    categories?: LocationCategory[];
    onLocationSelect?: (location: LocationItem) => void;
    onClose?: () => void;
    className?: string;
    openCurrentLocationDetail?: boolean;
    setOpenCurrentLocationDetail?: React.Dispatch<React.SetStateAction<boolean>>;
    handleOpenLocationDetail?: (data: any) => void;
    handleGoToLocation?: (sceneName: string) => void;
    currentLocation?: any;
    findMapSelect?: (map: any) => void;
    hoveredLocationId?: string | number | null;
    onLocationHover?: (locationId: string | number | null) => void;
    selectedLocationId?: string | number | null;
}

// Mock data based on the images provided
const MOCK_DATA: LocationCategory[] = [
    // ... Mock data structure would need to match new interface if used, but we mostly use API data
];

// Recursive component for rendering groups
const RecursiveGroup = ({
    category,
    level = 0,
    expandedIds,
    onToggle,
    onSelect,
    getPresentation,
    hoveredLocationId,
    onHover,
    selectedLocationId,
}: {
    category: LocationCategory;
    level?: number;
    expandedIds: string[];
    onToggle: (id: string) => void;
    onSelect: (item: LocationItem) => void;
    getPresentation?: (item: LocationItem) => ResolvedMarkerPresentation;
    hoveredLocationId?: string | number | null;
    onHover?: (id: string | number | null) => void;
    selectedLocationId?: string | number | null;
}) => {
    const isExpanded = expandedIds.includes(category.id);
    const hasChildren = category.children.length > 0 || category.items.length > 0;
    const paddingLeft = level * 16 + 16; // Indent based on level

    return (
        <>
            <div className={`${level === 0 ? 'border-b border-[var(--gen-outline)] last:border-0' : ''}`}>
                {/* Group Header */}
                <div
                    onClick={() => hasChildren && onToggle(category.id)}
                    className={`
                    flex items-center justify-between py-3 pr-4 cursor-pointer transition-colors
                    ${isExpanded ? 'bg-[var(--accordion-expand-bg)] text-[var(--accordion-expand-label)] hover:bg-[var(--accordion-expand-bg-hover)] border-b border-[var(--accordion-expand-outline)]' : 'bg-[var(--accordion-bg-default)] hover:bg-[var(--accordion-bg-hover)] text-[var(--accordion-label-default)]'}
                `}
                    style={{ paddingLeft: `${level == 0 ? paddingLeft : paddingLeft / 2}px` }}
                >
                    <span className={`font-medium text-sm ${isExpanded ? 'font-semibold' : ''} truncate flex-1 mr-2`}>
                        {category.title}
                    </span>
                    {hasChildren && (
                        <IconArrowHeadDown
                            stroke="var(--accordion-label-default)"
                            className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    )}
                </div>

                {/* Group Content */}
                <div
                    style={{ marginLeft: `16px` }}
                    className={`    
                    overflow-hidden transition-all duration-300 ease-in-out relative flex-1 border-l border-[var(--accordion-group-indicator-line)]
                    ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}
                `}
                >
                    {/* Render children groups first */}
                    {category.children.map(child => (
                        <RecursiveGroup
                            key={child.id}
                            category={child}
                            level={level + 1}
                            expandedIds={expandedIds}
                            onToggle={onToggle}
                            onSelect={onSelect}
                            getPresentation={getPresentation}
                            hoveredLocationId={hoveredLocationId}
                            onHover={onHover}
                            selectedLocationId={selectedLocationId}
                        />
                    ))}

                    {/* Render items in this group */}
                    {category.items.length > 0 && (
                        <>
                            <div className="bg-[var(--accordion-bg-default)]">
                                {category.items.map((item) => {
                                    const presentation = getPresentation ? getPresentation(item) : null;
                                    const hasBadge = presentation?.isNumbered && presentation.label !== null;
                                    const isActive = Boolean(presentation?.isActive);
                                    const isSelected = Boolean(selectedLocationId && String(selectedLocationId) === String(item.id));
                                    const isHovered = Boolean(hoveredLocationId && String(hoveredLocationId) === String(item.id));

                                    return (
                                        <div
                                            key={item.id}
                                            data-location-id={item.id}
                                            aria-current={isActive ? 'location' : isSelected ? 'true' : undefined}
                                            onMouseEnter={() => onHover?.(item.id)}
                                            onMouseLeave={() => onHover?.(null)}
                                            className={`flex items-center justify-between pr-4 transition-all duration-150 cursor-pointer ${
                                                isActive
                                                    ? 'bg-emerald-50/80 border-l-[3px] border-emerald-600 font-semibold text-emerald-950 shadow-xs'
                                                    : isHovered
                                                        ? 'bg-blue-50/50 text-[#E34935]'
                                                        : 'hover:text-[#E34935] hover:bg-[var(--accordion-bg-hover)]'
                                            }`}
                                            onClick={() => onSelect(item)}
                                        >
                                            <div
                                                className="flex items-center py-2 pr-4 text-sm text-[var(--accordion-label-default)] transition-colors truncate border-t border-dashed border-gray-100 first:border-t-0 flex-1 min-w-0"
                                                style={{ paddingLeft: `${16}px` }}
                                            >
                                                {hasBadge && (
                                                    <NumberBadgeView
                                                        label={presentation.label!}
                                                        border={presentation.border}
                                                        fill={presentation.fill}
                                                        text={presentation.text}
                                                        size={22}
                                                        className="mr-2 flex-shrink-0"
                                                    />
                                                )}
                                                <span className="truncate">{item.title}</span>
                                            </div>
                                            <IconArrowRight style={{ width: '20px', height: '20px' }} stroke="var(--button-primary-label-default)" />
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default function ListLocation({
    minimapId,
    mapMarkerDisplay,
    mapLocations,
    currentScene,
    categories: propCategories = MOCK_DATA,
    onLocationSelect,
    onClose,
    className = '',
    openCurrentLocationDetail,
    setOpenCurrentLocationDetail,
    handleOpenLocationDetail,
    handleGoToLocation,
    currentLocation,
    findMapSelect,
    hoveredLocationId,
    onLocationHover,
    selectedLocationId,
}: ListLocationProps) {
    const t = useTranslations();
    const { idPath } = useIdPath();
    const { locale } = useLanguage();
    const [searchQuery, setSearchQuery] = useState('');
    const [apiCategories, setApiCategories] = useState<LocationCategory[]>([]);
    const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<LocationItem | null>(null);
    const [loading, setLoading] = useState(false);

    const mapLocationsById = useMemo(() => {
        const map = new Map<string, any>();
        if (Array.isArray(mapLocations)) {
            mapLocations.forEach((loc) => {
                map.set(String(loc.id), loc);
            });
        }
        return map;
    }, [mapLocations]);

    const getPresentation = useCallback((item: LocationItem): ResolvedMarkerPresentation => {
        const matchedLoc = mapLocationsById.get(String(item.id));
        const configRaw = item.config ?? matchedLoc?.config;
        const fallbackNumber = item.display_number ?? item.index ?? matchedLoc?.index;
        const isDirectMatch = Boolean(currentScene && (isLocationMatchingScene(item, currentScene) || (matchedLoc && isLocationMatchingScene(matchedLoc, currentScene))));
        const isGroupMatch = Boolean(item._highlightCurrentByGroup || matchedLoc?._highlightCurrentByGroup);
        const isSceneActive = isDirectMatch || isGroupMatch;

        const isExplicitlySelected = selectedLocationId !== null && selectedLocationId !== undefined && (
            String(selectedLocationId) === String(item.id) || (matchedLoc && String(selectedLocationId) === String(matchedLoc.id))
        );

        const hasExplicitSelection = selectedLocationId !== null && selectedLocationId !== undefined && selectedLocationId !== '';
        const isActive = hasExplicitSelection ? isExplicitlySelected : isSceneActive;

        return resolveMarkerPresentation({
            mapMarkerDisplayRaw: mapMarkerDisplay,
            locationConfigRaw: configRaw,
            isActive,
            fallbackNumber,
        });
    }, [currentScene, mapLocationsById, mapMarkerDisplay, selectedLocationId]);

    // Filter categories recursively
    const filterCategoriesRecursive = useCallback((cats: LocationCategory[], query: string): LocationCategory[] => {
        const lowerQuery = query.toLowerCase().trim();
        return cats.map(cat => {
            // Check items matches
            const matchingItems = cat.items.filter(item => {
                const titleMatches = item.title?.toLowerCase().includes(lowerQuery);
                const presentation = getPresentation(item);
                const numMatches = presentation.isNumbered && presentation.label !== null
                    ? presentation.label.toLowerCase().includes(lowerQuery)
                    : false;
                return titleMatches || numMatches;
            });

            // Check children matches
            const matchingChildren = filterCategoriesRecursive(cat.children, query);

            // Check if category title itself matches
            const catMatches = cat.title?.toLowerCase().includes(lowerQuery);

            if (catMatches || matchingItems.length > 0 || matchingChildren.length > 0) {
                return {
                    ...cat,
                    items: matchingItems.length > 0 || catMatches ? (catMatches ? cat.items : matchingItems) : [],
                    children: matchingChildren
                };
            }
            return null;
        }).filter(Boolean) as LocationCategory[];
    }, [getPresentation]);

    const categories = useMemo(() => {
        const rawCats = apiCategories.length > 0 ? apiCategories : propCategories;
        if (!searchQuery.trim()) return rawCats;
        return filterCategoriesRecursive(rawCats, searchQuery);
    }, [apiCategories, filterCategoriesRecursive, propCategories, searchQuery]);

    const mapMarkerToItem = (marker: any): LocationItem => {
        // Get title from translations array, falling back to marker.title or placeholder
        const locLower = (locale || 'vn').toLowerCase();
        const matchedTranslation = marker.translations?.find((t: any) => {
            const tId = String(t.language_id || '').toLowerCase();
            return tId === locLower ||
                (locLower === 'en' && (tId === 'eng' || tId === 'en')) ||
                (locLower === 'vn' && (tId === 'vi' || tId === 'vie' || tId === 'vn'));
        });
        const translatedTitle = matchedTranslation?.title
            || marker.translations?.[0]?.title 
            || marker.title;
        const editmarker = {
            ...marker,
            title: translatedTitle || `${t('LOCATION')} ${marker.id}`,
        }
        return editmarker;
    };

    // Recursive parser for groups
    const parseGroups = (groups: any[]): LocationCategory[] => {
        return groups.map(group => ({
            id: group.id.toString(),
            title: group.title || `Group ${group.id}`,
            items: (group.markers || []).map(mapMarkerToItem),
            children: group.children ? parseGroups(group.children) : []
        }));
    };

    useEffect(() => {
        if (!minimapId || !idPath) return;

        const fetchNestedData = async () => {
            try {
                setLoading(true);
                const response = await minimapServices.getNestedGroupsWithMarkers(idPath, minimapId, locale);
                if (response.success && response.data) {
                    const data = response.data;
                    const newCategories: LocationCategory[] = [];

                    // Parse recursive groups
                    if (data.groups && data.groups.length > 0) {
                        const parsedGroups = parseGroups(data.groups);
                        newCategories.push(...parsedGroups);
                    }

                    // Handle ungrouped markers as a separate category
                    if (data.ungrouped_markers && data.ungrouped_markers.length > 0) {
                        newCategories.push({
                            id: 'ungrouped',
                            title: t('AUDIO_GROUP_EXPLORE') || 'Các địa điểm khác',
                            items: data.ungrouped_markers.map(mapMarkerToItem),
                            children: []
                        });
                    }

                    setApiCategories(newCategories);

                    // Auto expand first level if exists
                    if (newCategories.length > 0) {
                        // Expand all root IDs initially
                        setExpandedCategories(newCategories.map(c => c.id));
                    }
                }
            } catch (error) {
                console.error("Failed to fetch nested minimap data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchNestedData();
    }, [minimapId, idPath, locale]);

    // Toggle category expansion
    const toggleCategory = (categoryId: string) => {
        setExpandedCategories(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
    };

    // Auto-expand category containing hoveredLocationId or selectedLocationId and scroll row into view
    useEffect(() => {
        const activeTargetId = hoveredLocationId || selectedLocationId;
        if (!activeTargetId || apiCategories.length === 0) return;
        const targetId = String(activeTargetId);

        const neededIds = findCategoryChain(apiCategories, targetId);
        if (neededIds.length > 0) {
            setExpandedCategories((prev) => {
                const missing = neededIds.filter((id) => !prev.includes(id));
                return missing.length > 0 ? [...prev, ...missing] : prev;
            });
        }

        const rowElement = document.querySelector(`[data-location-id="${targetId}"]`);
        if (rowElement && typeof rowElement.scrollIntoView === 'function') {
            rowElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [hoveredLocationId, selectedLocationId, apiCategories]);

    // Handle selecting a location
    const handleSelectLocation = (location: LocationItem) => {
        if (onLocationSelect) {
            onLocationSelect(location);
            return;
        }

        if (location?.type == 'scene') {
            getSceneBySceneId(location?.entity_id);
        }

        if (location?.type == 'map') {
            findMapSelect?.(location?.entity_id);
        }

        if (location?.type === 'link') {
            try {
                const targetUrl = new URL(location.entity_id, window.location.href);
                if (targetUrl.protocol === 'http:' || targetUrl.protocol === 'https:') {
                    window.open(targetUrl.href, '_blank', 'noopener,noreferrer');
                }
            } catch (e) {
                console.error('Invalid link URL:', e);
            }
            return;
        }
    };

    // Handle back from detail view
    const handleBack = () => {
        if (openCurrentLocationDetail && setOpenCurrentLocationDetail) {
            setOpenCurrentLocationDetail(false)
        }
        setSelectedLocation(null);
    };

    // Get data scene
    const getSceneBySceneId = async (scene_id: string) => {
        try {
            const response = await sceneServices.getSceneById({
                id_path: idPath,
                language: locale,
                scene_id: scene_id,
                gender: "nu"
            });
            if (!response.error) {
                if (window?.innerWidth < 1024 && handleOpenLocationDetail) {
                    handleOpenLocationDetail(response?.data);
                } else {
                    setSelectedLocation(response?.data);
                }
            }
        } catch (err) {
            console.error('Error fetching Group View:', err);
        }
    };

    // Auto expand search results
    useEffect(() => {
        if (searchQuery.trim() && categories.length > 0) {
            // Helper to collect all IDs in tree
            const collectIds = (cats: LocationCategory[]): string[] => {
                let ids: string[] = [];
                cats.forEach(cat => {
                    ids.push(cat.id);
                    if (cat.children.length > 0) {
                        ids = [...ids, ...collectIds(cat.children)];
                    }
                });
                return ids;
            };
            setExpandedCategories(collectIds(categories));
        }
    }, [searchQuery, categories]);

    useEffect(() => {
        if (openCurrentLocationDetail) {
            if (currentLocation) {
                setSelectedLocation(currentLocation);
            } else {
                setSelectedLocation((window as any)?.currentSceneInfo);
            }
        } else if (onLocationSelect) {
            setSelectedLocation(null);
        }
    }, [openCurrentLocationDetail, currentLocation, onLocationSelect]);

    // Render Detail View
    if (selectedLocation) {
        if (window?.innerWidth >= 1024) {
            return (
                <LocationDetail
                    location={selectedLocation}
                    onBack={handleBack}
                    onGoToLocation={handleGoToLocation}
                    className={className}
                />
            );
        }
    }

    // Render List View
    return (
        <>
            <div style={{ margin: '0 auto 10px' }} onClick={() => onClose && onClose()} className="cursor-pointer w-[30px] h-[30px] lg:hidden flex items-center justify-center p-1 shadow-lg rounded-full bg-[var(--button-primary-bg-default)]">
                <IconX style={{ width: '13px', height: '13px' }} stroke="var(--button-primary-label-default)" />
            </div>
            <div
                className={`flex flex-col h-[calc(75vh-40px)] lg:h-full bg-[var(--accordion-bg-default)] lg:w-full shadow-lg ${className} relative`}
                style={{ touchAction: 'pan-y', overscrollBehavior: 'contain' }}
            >
                {/* Search Header */}
                <div className="shadow-[0px_2px_8px_0px_#00000040] lg:shadow-none z-100 absolute top-0 left-0 right-0 px-4 h-[60px] border-b border-[var(--gen-outline)] bg-[var(--gen-search-bar-bg)] flex items-center gap-2">
                    <div className="relative flex-1">
                        <IconSearch className="absolute left-0 top-1/2 -translate-y-1/2 w-6 h-6" stroke="#656565" />
                        <input
                            type="text"
                            placeholder={t('SEARCH_PLACEHOLDER')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-4 py-1 border-none rounded-lg text-sm text-[var(--gen-label-inactive)] focus:outline-none placeholder-[var(--gen-label-inactive)]"
                        />
                    </div>
                    {
                        searchQuery.length > 0 && (
                            <button onClick={() => setSearchQuery("")} className="p-2 hover:bg-gray-100 rounded-full text-gray-500 md:hidden">
                                <X className="w-5 h-5" />
                            </button>
                        )
                    }
                </div>

                {/* Content List */}
                <div
                    className="flex-1 overflow-y-auto no-scrollbar pt-[61px] h-[calc(100%-61px)]"
                    style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
                >
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--gen-primary-color)]"></div>
                        </div>
                    ) : categories.length > 0 ? (
                        categories.map((category) => (
                            <RecursiveGroup
                                key={category.id}
                                category={category}
                                expandedIds={expandedCategories}
                                onToggle={toggleCategory}
                                onSelect={handleSelectLocation}
                                getPresentation={getPresentation}
                                hoveredLocationId={hoveredLocationId}
                                onHover={onLocationHover}
                                selectedLocationId={selectedLocationId}
                            />
                        ))
                    ) : (
                        <div className="p-8 text-center text-gray-400 text-sm">
                            {t('MATERIAL_DATA_MISSING')}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
